import { useContext, useRef, useState } from 'react'
import { useAsyncEffect } from 'utils'
import { MediaSprite, TextStyle } from '../../../../types'
import { ArtboardContext } from '../../board/context'

const PADDING = 10
const LINE_HEIGHT_MULTIPLIER = 1.2

const useTextSprite = (text: string, style: TextStyle, scale: number = 1): MediaSprite | null => {
    const { align = 'center', color, fontFamily, fontSize, fontWeight, strokeColor = '', strokeSize = 0 } = style

    const board = useContext(ArtboardContext)
    const [sprite, setSprite] = useState<MediaSprite | null>(null)
    const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))

    useAsyncEffect(async () => {
        // preload font
        if (document && (document as any).fonts) {
            try {
                await (document as any).fonts.load(`${fontSize}px ${fontFamily}`)
            } catch (err) {}
        }

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const lineHeight = Math.round(fontSize * LINE_HEIGHT_MULTIPLIER)
        const lineOffsetY = Math.round((lineHeight - fontSize) / 2)

        if (context) {
            context.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            const colors = color.split(',') // account for multiple color values
            const strokeColors = strokeColor.split(',') // account for multiple color values
            const lines = text.split('\n')
            const longestLine = lines.reduce((a, b) => (a.length > b.length ? a : b))
            const textWidth = Math.ceil(context.measureText(longestLine).width + (strokeSize * 2) / 2)
            const frameWidth = textWidth + PADDING * 2
            const frameHeight = lineHeight * lines.length + PADDING * 2

            canvas.width = Math.round(frameWidth * scale)
            canvas.height = Math.round(frameHeight * scale)

            context.save()
            context.translate(Math.round(canvas.width / 2), PADDING * scale)
            context.font = `${fontWeight} ${Math.round(fontSize * scale)}px ${fontFamily}`
            context.lineCap = 'round'
            context.lineJoin = 'round'
            context.lineWidth = Math.round(strokeSize * 2 * scale)
            context.textAlign = 'center'
            context.textBaseline = 'top'

            // text color
            if (colors.length > 1) {
                const gradient = context.createLinearGradient(0, canvas.height, canvas.width, 0)

                colors.forEach((gradientColor, i) => {
                    gradient.addColorStop(i / colors.length, gradientColor)
                })

                context.fillStyle = gradient
            } else {
                context.fillStyle = color
            }

            // stroke color
            if (strokeColors.length > 1) {
                const strokeGradient = context.createLinearGradient(0, canvas.height, canvas.width, 0)

                strokeColors.forEach((gradientColor, i) => {
                    strokeGradient.addColorStop(i / colors.length, gradientColor)
                })

                context.strokeStyle = strokeGradient
            } else {
                context.strokeStyle = strokeColor
            }

            lines.forEach((line, i) => {
                let lineX = 0
                const lineY = (i * lineHeight + lineOffsetY) * scale
                const lineWidthDiff = context.measureText(longestLine).width - context.measureText(line).width

                switch (align) {
                    case 'center':
                        lineX = 0
                        break
                    case 'left':
                        lineX = -Math.round(lineWidthDiff / 2)
                        break
                    case 'right':
                        lineX = Math.round(lineWidthDiff / 2)
                        break
                }

                if (strokeSize) {
                    context.strokeText(line, lineX, lineY)
                }

                context.fillText(line, lineX, lineY)
            })

            context.restore()
        }

        setSprite({
            canvas,
            columns: 1,
            duration: 0,
            frameHeight: canvas.height,
            frameIndexes: [0],
            frameWidth: canvas.width,
            numFrames: 1,
            rows: 1,
        })
    }, [align, board.width, color, fontFamily, fontSize, fontWeight, scale, strokeColor, strokeSize, text])

    return sprite
}

export default useTextSprite
